import TB from '@tiger/bridge';
import COOKIE from 'cookick';
import lodash from 'lodash';
import { action, computed, observable } from 'mobx';
import { RegisterTypeEnum } from 'types/common';
import { AccountProcessDataType, CustomerInfoType, CustomerProcessStatusEnum } from 'types/customer';
import { OauthErrorCodeEnum } from 'types/error';
import { AccountItemType, ApiWebUserData, ApiWebUserDataWithCustomerId, CurrentUserType, UserInfo } from 'types/user';
import API from 'utils/API';
import { getCustomerIdFromUrl } from 'utils/helper';
import http, { HttpError } from 'utils/http';
import track from 'utils/track';
import { chooseCurrentAccount, fixDataToLocal } from './helper';

interface IParams {
    [key: string]: any;
}

const SECURITY = 'TIGER_US_PORTAL_SECURITY';

const DOMAIN = window.location.hostname.split('.').slice(-2).join('.');

export const FB_PIXEL_ID_MAP = {
    old: '203661610779030',
    new: '4348856555174805'
};

export class Security {
    // 用户级别的信息
    // 后续如果有新增内容, 如非必须, 不再建议扩充currentUser属性(包括currentUser下一级中的属性, 也尽量不要再往里面塞多余的内容)
    // 否则会导致currentUser承载的内容越来越多, 可维护性极差
    // 后续如果不是/web/user/status接口里面拿到的内容,或者该接口衍生出的内容, 不再建议放在currentUser中, 可以考虑新建实例属性
    // 如果必须放进currentUser中, 必须在WebUserDataType类型中详细注释说明字段的含义(作用和需要使用的地方)和来源(接口名和设置该字段的地方)
    @observable
    currentUser: CurrentUserType | null = null; // 用户状态

    @observable
    userInfo: UserInfo;

    // 用户的开户资料(账户级别)
    @observable
    customerInfo: CustomerInfoType;

    @observable
    processData: AccountProcessDataType | undefined;

    ACCESS_TOKEN: string | null;

    // 获取当前账户
    @computed
    get currentAccount() {
        return this.currentUser?.currentAccount;
    }

    @computed
    get isLogin() {
        return this.currentUser !== null;
    }

    // 获得当前账户的customerId
    @computed
    get currentCustomerId() {
        return this.currentAccount?.customerId;
    }

    // 开户进度
    @computed
    get currentProcessStatus() {
        return this.processData?.status.status || this.currentAccount?.status || CustomerProcessStatusEnum.UNACCOUNT;
    }

    @action
    setCustomerInfo(data: CustomerInfoType) {
        this.customerInfo = data;
    }

    // 设置当前账户
    @action
    setCurrentAccount = (params: Partial<AccountItemType>) => {
        if (this.currentUser) {
            this.currentUser.currentAccount = {
                ...this.currentUser.currentAccount,
                ...params
            };
        }
    };

    saveUserLoginInfo(token) {
        COOKIE.setCookie(SECURITY, token, {
            maxAge: 3600 * 24 * 7,
            path: '/',
            domain: DOMAIN,
            secure: !__DEV__
        });

        localStorage.setItem(
            SECURITY,
            JSON.stringify({
                token
            })
        );
    }

    async ready() {
        try {
            await TB.ready();

            // 是老虎app的话，就通过TigerBridge尝试去获取token
            const TigerBridge = window.TigerBridge;

            // 获取token
            this.ACCESS_TOKEN = TigerBridge.getAccessToken();
            await this.getStatus();
        } catch (error) {
            // 和us-portal在同一域名下，因此可以直接尝试获取token
            const authLocalStorage = localStorage.getItem(SECURITY);
            const authCookie = COOKIE.getCookie(SECURITY);
            // cookie中的token优先级最高，其次是localStorage中的，都不存在则为null
            let tempToken: null | string = null;

            try {
                tempToken = authCookie ? authCookie : authLocalStorage ? JSON.parse(authLocalStorage).token : null;
            } catch (e) {}

            // 如果tempToken存在，则证明已经登陆过，则获取用户状态信息
            if (tempToken) {
                this.ACCESS_TOKEN = tempToken;
                await this.getStatus();
            } else {
                this.clearAuth();
            }
        }
    }

    // 获取用户状态信息
    @action
    async getStatus() {
        const urlCustomerId = getCustomerIdFromUrl();
        let currentAccount: AccountItemType;
        // /web/user/status接口中的数据, 不包含衍生出的currentAccount
        let apiUserData: Omit<CurrentUserType, 'currentAccount'>;

        if (urlCustomerId) {
            const tempResult = await API.OAUTH_USER.webUserStatus
                .get<ApiWebUserDataWithCustomerId>({
                    params: {
                        customerId: urlCustomerId
                    }
                })
                .then(res => res.data);

            currentAccount = lodash.omit(tempResult, 'userInfo', 'tradePermission');
            apiUserData = lodash.pick(tempResult, 'userInfo', 'tradePermission') as any;

            // customerId不匹配的时候, server会过滤掉customerId对应的用户信息
            if (!currentAccount.customerId) {
                const error = new Error(__('您的登录信息不匹配，请您使用正确的账号重新登录')) as HttpError;

                error.error_code = OauthErrorCodeEnum.MISS_MATCH_CUSTOMER_ID;

                throw error;
            }

            this.userInfo = tempResult.userInfo;
        } else {
            apiUserData = await http.get<ApiWebUserData>(API.OAUTH_USER.webUserStatus()).then(res => res.data);
            currentAccount = chooseCurrentAccount(apiUserData as ApiWebUserData);

            if (apiUserData) {
                this.userInfo = apiUserData.userInfo;
            }
        }

        // 设置埋点系统中独一无二的用户id, 开户系统中使用uuid来进行标识
        track.setEventUUId(this.userInfo.uuid);

        const { customerId } = currentAccount;

        if (customerId) {
            await Promise.all([this.getOpenProcess(customerId), this.getCustomerInfo(customerId)]);
        }

        this.currentUser = {
            ...(apiUserData || {}),
            currentAccount
        };

        return this.currentUser;
    }

    /**
     * @description 获取用户的customer级别(用户的当前账户)的详细信息
     *              已经封装进了withCustomerInfo高阶组件中, 可以优先考虑直接使用高阶组件
     * @param {number} customerId 只有两种场景需要带上customerId参数:
     *      1. 项目初始化的getStatus方法中
     *      2. 提交完open接口之后, 拿到了新的customerId
     */
    getCustomerInfo = (customerId?: number): Promise<CustomerInfoType> => {
        return http
            .get<CustomerInfoType>(API.CUSTOMER.customerInfo(), {
                params: {
                    customer_id: customerId || this.currentCustomerId
                }
            })
            .then(({ data }) => {
                const curCustomerInfo = fixDataToLocal(data);

                this.setCustomerInfo(curCustomerInfo);

                return curCustomerInfo;
            });
    };

    /**
     * @description 开户提交后，服务端会返回对应的customer_id，仅在这一种情况下使用，因为会覆盖customer当前的属性
     * @param {number} customerId 只有一种场景请求process接口的时候需要带上customerId, 即提交完open接口之后, 拿到了新的customerId
     */
    getOpenProcess = async (customerId?: number) => {
        const curProcessData = await http
            .get<AccountProcessDataType>(API.CUSTOMER.process(), {
                params: {
                    customer_id: customerId || this.currentCustomerId
                }
            })
            .then(res => res.data);

        this.processData = curProcessData;

        /**
         * 使用了新的customerId, 则当前账户中的customerId也要更新
         * 原因: 只有customerId和当前牌照环境相同的时候, 请求process接口才能够拿到正确的数据
         *      那么既然这时候请求process能够拿到正确数据, 说明牌照环境已经切换了, 这时候是有了对应牌照的customerId, 然后发起了首次请求
         *      所以这时候应该把当前账户中的customerId更新为新的customerId(新的customerId存在的情况下)
         *  这里还需要多两个判断:
         * 1. 参数中传的customerId存在的时候才需要更新
         * 2. 与当前customerId相等的判断, 是为了防止使用不当而做的拦截, 相同的时候, 也不需要更新
         */
        if (this.currentUser && customerId && customerId !== this.currentCustomerId) {
            this.setCurrentAccount({
                customerId
            });
        }

        return curProcessData;
    };

    /**
     * 登录
     * @param {object} params 参数
     */
    login(data: IParams) {
        return http
            .post(API.OAUTH.signin(), data, {
                noToken: true,
                withCredentials: true
            })
            .then(resp => {
                // 将token和status存放到store
                this.ACCESS_TOKEN = resp.data.access_token;

                this.saveUserLoginInfo(this.ACCESS_TOKEN);

                return this.getStatus();
            });
    }

    /**
     * 注册账号
     * @param {object} params 参数
     */
    signup(data: IParams, grant_type: RegisterTypeEnum) {
        return http
            .post(grant_type === RegisterTypeEnum.PHONE ? API.OAUTH.signupV2() : API.OAUTH.signup(), {
                ...data,
                grant_type,
                ...(grant_type === RegisterTypeEnum.PHONE
                    ? {}
                    : {
                          skip_verify_code: true
                      })
            })
            .then(resp => {
                this.ACCESS_TOKEN = resp.data.access_token;

                this.saveUserLoginInfo(this.ACCESS_TOKEN);
            });
    }

    esopSignup(data: IParams, grant_type) {
        return http
            .post(API.OAUTH.signup(), {
                ...data,
                grant_type
            })
            .then(resp => {
                this.ACCESS_TOKEN = resp.data.access_token;

                this.saveUserLoginInfo(this.ACCESS_TOKEN);
            });
    }

    clearAuth() {
        localStorage.removeItem(SECURITY);

        COOKIE.delCookie(SECURITY, { path: '/', domain: DOMAIN });

        this.ACCESS_TOKEN = null;

        this.currentUser = null;
    }
}

export default new Security();

export * from './helper';
